/**
 * React bindings for @hanzo/iam.
 *
 * Provides a context provider, auth hooks, org/project switching, and the
 * embedded `<Login>` component — the DEFAULT login UI apps should mount.
 * `<Login>` (re-exported here from `@hanzo/iam/views`) keeps every method
 * in-place: social via popup + postMessage, wallet via inline SIWx,
 * email/password/OTP via PKCE-bound code. It never full-page redirects to
 * the hosted login page. `signinRedirect` (surfaced as `useIam().login`)
 * remains available only as an explicit standalone fallback.
 *
 * @example
 * ```tsx
 * import { IamProvider, Login, useIam } from '@hanzo/iam/react'
 *
 * function App() {
 *   return (
 *     <IamProvider config={{
 *       serverUrl: 'https://iam.hanzo.ai',
 *       clientId: 'my-app',
 *       redirectUri: `${window.location.origin}/auth/callback`,
 *     }}>
 *       <MyApp />
 *     </IamProvider>
 *   )
 * }
 *
 * function MyApp() {
 *   const { user, isAuthenticated } = useIam()
 *   // Embedded, in-place — the default. Not a redirect.
 *   if (!isAuthenticated)
 *     return <Login serverUrl="https://iam.hanzo.ai" clientId="my-app"
 *                   redirectUri={`${location.origin}/auth/callback`} state=""
 *                   providers={['google','github','wallet']} />
 *   return <div>Welcome, {user?.displayName}</div>
 * }
 * ```
 *
 * @packageDocumentation
 */

import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
// The design system and the brand mark, both data-only entry points (no React):
// one visual language for the control on every surface.
import { cssVars, motion } from "@hanzo/design";
import { MARK_PATHS, MARK_VIEWBOX } from "@hanzo/logo/logos";
import { IAM } from "./browser.js";
import type { IAMConfig } from "./browser.js";
import type { User, Organization, Project } from "./types.js";
import {
  applyTheme,
  claimsOf,
  looksLikeId,
  membershipsFromToken,
  readThemeMode,
  resolveIdentity,
  themeStorageKey,
} from "./identity.js";
import type { IamIdentity, ThemeMode } from "./identity.js";
import type { IAMToken } from "./browser.js";

/**
 * The longest delay `setTimeout` can hold. It keeps the delay in a signed
 * 32-bit integer, so anything past this wraps to a negative and the timer
 * fires on the next tick instead of in three weeks. A long-lived token dates
 * its refresh well beyond the ceiling, and firing early would refresh — and,
 * when that refresh fails, sign out — a session with weeks left on it.
 */
const MAX_DELAY = 2_147_483_647;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface IamProviderProps {
  /** Browser IAM SDK configuration. */
  config: IAMConfig;
  /** Auto-initialize on mount (check stored tokens). Default: true. */
  autoInit?: boolean;
  /** Called when authentication state changes. */
  onAuthChange?: (authenticated: boolean) => void;
  children: ReactNode;
}

export interface IamContextValue {
  /** The underlying IAM instance for advanced use. */
  sdk: IAM;
  /** The IAM configuration. */
  config: IAMConfig;
  /** Authenticated user (null if not logged in). */
  user: User | null;
  /** Whether the user is currently authenticated. */
  isAuthenticated: boolean;
  /** Whether initial auth check is in progress. */
  isLoading: boolean;
  /** Current access token (null if not authenticated). */
  accessToken: string | null;
  /**
   * Explicit standalone FALLBACK: full-page redirect to the hosted login
   * page. Not the default — prefer the embedded `<Login>` component or
   * `loginPopup` so login stays in-place.
   */
  login: (params?: { additionalParams?: Record<string, string> }) => Promise<void>;
  /** In-place social login via popup + postMessage. Resolves once signed in. */
  loginPopup: (params?: { width?: number; height?: number }) => Promise<void>;
  /** Handle OAuth callback — call on your /auth/callback route. */
  handleCallback: (callbackUrl?: string) => Promise<IAMToken>;
  /**
   * Sign out: end the session at the issuer AND clear this browser. Resolves
   * only if the issuer could not be reached — on the ordinary path the document
   * is already navigating to the end-session endpoint.
   */
  logout: () => Promise<void>;
  /** Last auth error, if any. */
  error: Error | null;
}

/**
 * Find organizations by name, beyond the ones already in hand.
 *
 * `organizations` is the caller's OWN memberships — everything a normal user can
 * act in, and the whole story for a normal app. An ADMIN surface is the exception:
 * it can enter ANY tenant, and that set is a server-side table too large to hand
 * over, so it is searched rather than listed. Supplying this turns the switcher
 * into a search field over the host's own cross-tenant list; omitting it leaves the
 * memberships-only control unchanged.
 *
 * The host owns the reach: the SDK never asks for orgs the caller cannot see, and
 * an implementation is expected to be gated (a non-admin gets their own orgs back).
 */
export type OrgFinder = (query: string) => Promise<Organization[]>;

export interface OrgState {
  /** All organizations the user belongs to. */
  organizations: Organization[];
  /** Coarse role per org, from the token's `orgs` claim (`org` → `role`). */
  roles: Record<string, string>;
  /** Role in the currently selected organization, when the token carries one. */
  currentRole: string | null;
  /** Currently selected organization. */
  currentOrg: Organization | null;
  /** Currently selected org ID. */
  currentOrgId: string | null;
  /** Switch to a different organization. */
  switchOrg: (orgId: string) => void;
  /** All projects for the current organization. */
  projects: Project[];
  /** Currently selected project. */
  currentProject: Project | null;
  /** Currently selected project ID within the org. */
  currentProjectId: string | null;
  /** Switch to a different project (null to clear). */
  switchProject: (projectId: string | null) => void;
  /** Whether organizations are loading. */
  isLoading: boolean;
  /** Optional: reach organizations beyond `organizations`. See `OrgFinder`. */
  findOrgs?: OrgFinder;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const IamContext = createContext<IamContextValue | null>(null);
IamContext.displayName = "IamContext";

// Storage keys for tenant persistence. The SESSION's keys are deliberately not
// here: the expiry used to be, as a second copy of a name browser.ts already
// owned, read out of a second copy of the storage resolution. Ask `sdk` for
// anything the session knows — it holds the one storage this provider's tokens
// were actually written to.
const STORAGE_ORG_KEY = "hanzo_iam_current_org";
const STORAGE_PROJECT_KEY = "hanzo_iam_current_project";

// ---------------------------------------------------------------------------
// IamProvider
// ---------------------------------------------------------------------------

/**
 * Root provider for Hanzo IAM in React applications.
 *
 * Wrap your app (or a subtree) with this provider to enable IAM auth.
 * Manages the IAM instance, token lifecycle, and auth state.
 */
export function IamProvider(props: IamProviderProps) {
  const { config, autoInit = true, onAuthChange, children } = props;

  const sdk = useMemo(
    () => new IAM(config),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config.serverUrl, config.clientId, config.redirectUri],
  );

  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(autoInit);
  const [accessToken, setAccessToken] = useState<string | null>(
    sdk.getAccessToken(),
  );
  const [error, setError] = useState<Error | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Schedule token refresh ~60s before expiry
  const scheduleRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    if (sdk.isTokenExpired()) return;

    const expiresAt = sdk.getExpiresAt();
    if (expiresAt === null) return;

    const msUntilRefresh = expiresAt - Date.now() - 60_000;
    if (msUntilRefresh > MAX_DELAY) {
      // Sleep to the ceiling and re-decide from there, never past it.
      refreshTimerRef.current = setTimeout(scheduleRefresh, MAX_DELAY);
      return;
    }
    if (msUntilRefresh <= 0) {
      sdk
        .refreshAccessToken()
        .then((tokens) => {
          setAccessToken(tokens.accessToken);
          scheduleRefresh();
        })
        .catch(() => {
          setIsAuthenticated(false);
          setUser(null);
          setAccessToken(null);
        });
      return;
    }

    refreshTimerRef.current = setTimeout(async () => {
      try {
        const tokens = await sdk.refreshAccessToken();
        setAccessToken(tokens.accessToken);
        scheduleRefresh();
      } catch {
        setIsAuthenticated(false);
        setUser(null);
        setAccessToken(null);
      }
    }, msUntilRefresh);
  }, [sdk]);

  // Auto-init: check stored tokens on mount
  useEffect(() => {
    if (!autoInit) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const init = async () => {
      try {
        const token = await sdk.getValidAccessToken();
        if (cancelled) return;
        if (token) {
          setAccessToken(token);
          setIsAuthenticated(true);
          try {
            const info = await sdk.getUserInfo();
            if (!cancelled) setUser(info as unknown as User);
          } catch {
            // Token valid but userinfo failed — still authenticated
          }
          scheduleRefresh();
          onAuthChange?.(true);
        } else {
          onAuthChange?.(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          onAuthChange?.(false);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdk, autoInit]);

  // Cleanup refresh timer on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  // Complete authentication after login/callback
  const completeAuth = useCallback(
    async (tokens: IAMToken) => {
      setAccessToken(tokens.accessToken);
      setIsAuthenticated(true);
      try {
        const info = await sdk.getUserInfo();
        setUser(info as unknown as User);
      } catch {
        // ok — token valid, userinfo is optional
      }
      scheduleRefresh();
      onAuthChange?.(true);
    },
    [sdk, scheduleRefresh, onAuthChange],
  );

  const login = useCallback(
    async (params?: { additionalParams?: Record<string, string> }) => {
      setError(null);
      await sdk.signinRedirect(params);
    },
    [sdk],
  );

  const loginPopup = useCallback(
    async (params?: { width?: number; height?: number }) => {
      setError(null);
      try {
        const tokens = await sdk.signinPopup(params);
        await completeAuth(tokens);
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      }
    },
    [sdk, completeAuth],
  );

  const handleCallback = useCallback(
    async (callbackUrl?: string) => {
      setError(null);
      try {
        const tokens = await sdk.handleCallback(callbackUrl);
        await completeAuth(tokens);
        return tokens;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      }
    },
    [sdk, completeAuth],
  );

  /**
   * Sign out — which is TWO things, and this used to do only one.
   *
   * `clearTokens()` empties this browser and leaves the issuer's session
   * untouched, so the next sign-in mints a code with no prompt and the person
   * lands straight back where they were: signing out appears to do nothing.
   * `sdk.logout()` is the whole gesture — it revokes the refresh and access
   * tokens, clears this machine, and then LEAVES on a navigation to the
   * end-session endpoint, which is the only way the issuer's `SameSite=Lax`
   * cookie is presented to it.
   *
   * Nothing is published on the success path, deliberately. Announcing a
   * signed-out state here would flip every auth gate mounted above this hook,
   * and a gate's reaction is its own `location` assignment — it lands after
   * this one and supersedes it, so the browser never reaches the issuer. That
   * race is exactly the bug this is fixing; the document is being replaced, so
   * there is no state worth publishing on the way out.
   */
  const logout = useCallback(async () => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    try {
      localStorage.removeItem(STORAGE_ORG_KEY);
      localStorage.removeItem(STORAGE_PROJECT_KEY);
    } catch {
      /* ok */
    }
    try {
      await sdk.logout();
    } catch {
      // The issuer could not be reached. Leaving THIS machine is not
      // best-effort, so drop the tokens and say so — a UI still claiming a
      // session no token backs is the worse of the two failures.
      sdk.clearTokens();
      setUser(null);
      setIsAuthenticated(false);
      setAccessToken(null);
      setError(null);
      onAuthChange?.(false);
    }
  }, [sdk, onAuthChange]);

  const value = useMemo<IamContextValue>(
    () => ({
      sdk,
      config,
      user,
      isAuthenticated,
      isLoading,
      accessToken,
      login,
      loginPopup,
      handleCallback,
      logout,
      error,
    }),
    [
      sdk,
      config,
      user,
      isAuthenticated,
      isLoading,
      accessToken,
      login,
      loginPopup,
      handleCallback,
      logout,
      error,
    ],
  );

  return createElement(IamContext.Provider, { value }, children);
}

// ---------------------------------------------------------------------------
// useIam
// ---------------------------------------------------------------------------

/**
 * Access Hanzo IAM auth state and methods.
 * Must be used within an `<IamProvider>`.
 */
export function useIam(): IamContextValue {
  const ctx = useContext(IamContext);
  if (!ctx) {
    throw new Error("useIam() must be used within an <IamProvider>");
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// useOrganizations
// ---------------------------------------------------------------------------

/**
 * Manage organization switching, derived from the JWT `sub`/`owner`
 * claims. Project state is preserved as an empty list — apps that
 * need full org/project listings must query their own admin API.
 *
 * Selection is persisted to localStorage.
 */
export function useOrganizations(): OrgState {
  const { isAuthenticated, accessToken } = useIam();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [roles, setRoles] = useState<Record<string, string>>({});

  const [currentOrgId, setCurrentOrgId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_ORG_KEY);
    } catch {
      return null;
    }
  });

  const [currentProjectId, setCurrentProjectId] = useState<string | null>(
    () => {
      try {
        return localStorage.getItem(STORAGE_PROJECT_KEY);
      } catch {
        return null;
      }
    },
  );

  // Read the membership set out of the token: `orgs` (home first, with roles),
  // falling back to the primary org in `owner`/`sub` for a pre-claim token. No API
  // call, and never the legacy /api/get-organizations admin surface.
  useEffect(() => {
    if (!isAuthenticated || !accessToken) {
      setOrganizations([]);
      setRoles({});
      return;
    }
    const { organizations: names, roles: claimedRoles } = membershipsFromToken(accessToken);
    setOrganizations(
      names.map((name) => ({ owner: "admin", name, displayName: name }) as Organization),
    );
    setRoles(claimedRoles);

    // Home org is the default; a remembered selection the token no longer carries
    // is stale (membership was revoked) and must not survive.
    const home = names[0];
    if (home && (!currentOrgId || !names.includes(currentOrgId))) {
      setCurrentOrgId(home);
      try {
        localStorage.setItem(STORAGE_ORG_KEY, home);
      } catch {
        /* ok */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, accessToken]);

  const projects: Project[] = [];
  const currentProject: Project | null = null;

  const currentOrg = useMemo(
    () => organizations.find((o) => o.name === currentOrgId) ?? null,
    [organizations, currentOrgId],
  );

  const switchOrg = useCallback((orgId: string) => {
    setCurrentOrgId(orgId);
    setCurrentProjectId(null);
    try {
      localStorage.setItem(STORAGE_ORG_KEY, orgId);
      localStorage.removeItem(STORAGE_PROJECT_KEY);
    } catch {
      /* ok */
    }
  }, []);

  const switchProject = useCallback((projectId: string | null) => {
    setCurrentProjectId(projectId);
    try {
      if (projectId) {
        localStorage.setItem(STORAGE_PROJECT_KEY, projectId);
      } else {
        localStorage.removeItem(STORAGE_PROJECT_KEY);
      }
    } catch {
      /* ok */
    }
  }, []);

  return {
    organizations,
    roles,
    currentRole: currentOrgId ? (roles[currentOrgId] ?? null) : null,
    currentOrg,
    currentOrgId,
    switchOrg,
    projects,
    currentProject,
    currentProjectId,
    switchProject,
    isLoading: false,
  };
}

// ---------------------------------------------------------------------------
// useIamToken
// ---------------------------------------------------------------------------

/**
 * Hook that provides a valid access token with auto-refresh capability.
 * Returns null while loading or if not authenticated.
 */
export function useIamToken(): {
  token: string | null;
  isValid: boolean;
  refresh: () => Promise<string | null>;
} {
  const { sdk, accessToken, isAuthenticated } = useIam();

  const refresh = useCallback(async () => {
    try {
      return await sdk.getValidAccessToken();
    } catch {
      return null;
    }
  }, [sdk]);

  return {
    token: accessToken,
    isValid: isAuthenticated && !!accessToken && !sdk.isTokenExpired(),
    refresh,
  };
}

// Re-export context for advanced use
export { IamContext };

// The identity helpers the hooks are built on — usable without React too.
export {
  claimsOf,
  looksLikeId,
  membershipsFromToken,
  resolveIdentity,
  applyTheme,
  readThemeMode,
};
export type { IamIdentity, ThemeMode };

// ---------------------------------------------------------------------------
// Paint
//
// The control ships its OWN stylesheet, scoped to its own class names, whose
// every value is a design token with the design system's authored literal as the
// fallback. Two reasons it is not utility classes and not bare inline styles:
//
//  - A distributed component must never paint with utility class names. Tailwind
//    does not scan node_modules, so `bg-muted`/`px-3` reach a consumer with no
//    rule behind them and render transparent, unpadded and unstacked — which is
//    exactly what happened in a host that ships no Tailwind at all.
//  - Inline styles cannot express `:hover`/`:focus-visible`, and a menu row with
//    no hover state is not a menu.
//
// Because the values are `var(--token, literal)`, the control looks right in an
// app that has adopted the tokens AND identical in one that has not — no
// stylesheet import, no build step, no class-name contract. `classNames` stays
// the opt-in override for an app that wants its own.
// ---------------------------------------------------------------------------

/** A token as a paintable value: the live variable over its authored literal. */
const tok = (name: string): string => {
  const literal = (cssVars as Record<string, string | undefined>)[`--${name}`];
  return literal ? `var(--${name}, ${literal})` : `var(--${name})`;
};

const MENU_STYLE_ID = "hanzo-iam-menu-style";

/** The control's own rules, once per document. Idempotent, browser-only. */
function injectMenuCss(): void {
  if (typeof document === "undefined" || document.getElementById(MENU_STYLE_ID)) return;
  const border = `1px solid ${tok("border")}`;
  const el = document.createElement("style");
  el.id = MENU_STYLE_ID;
  el.textContent = `
.hz-iam-menu{font-family:inherit;font-size:${tok("text-sm")};line-height:${tok("leading-sm")};color:${tok("foreground")};background:${tok("popover")};border:${border};border-radius:${tok("radius-lg")};box-shadow:${tok("shadow-floating")};overflow:hidden}
.hz-iam-sec{border-bottom:${border};padding:${tok("space-2")} ${tok("space-3")}}
.hz-iam-grp{padding:${tok("space-1")} 0}
.hz-iam-grp+.hz-iam-grp{border-top:${border}}
.hz-iam-row{display:flex;width:100%;box-sizing:border-box;align-items:center;gap:${tok("space-2")};padding:${tok("space-1")} ${tok("space-3")};min-height:28px;text-align:left;font:inherit;color:${tok("foreground")};background:none;border:0;cursor:pointer;text-decoration:none}
.hz-iam-row:hover,.hz-iam-row:focus-visible{background:${tok("muted")};outline:none}
.hz-iam-row[data-tone=danger]{color:${tok("state-error-text")}}
.hz-iam-row[data-tone=accent]{color:${tok("state-success")}}
.hz-iam-row[aria-current=true]{background:${tok("muted")}}
.hz-iam-grow{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.hz-iam-lbl{padding:${tok("space-2")} ${tok("space-3")} ${tok("space-1")};font-size:${tok("text-xs")};font-weight:${tok("weight-medium")};color:${tok("muted-foreground")}}
.hz-iam-name{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-weight:${tok("weight-medium")}}
.hz-iam-quiet{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:${tok("text-xs")};color:${tok("muted-foreground")}}
.hz-iam-find{display:block;width:100%;box-sizing:border-box;margin-bottom:${tok("space-1")};padding:${tok("space-1")} ${tok("space-2")};font:inherit;font-size:${tok("text-xs")};color:${tok("foreground")};background:${tok("background")};border:${border};border-radius:${tok("radius-md")}}
.hz-iam-find::placeholder{color:${tok("muted-foreground")}}
.hz-iam-find:focus-visible{outline:none;border-color:${tok("ring")}}
.hz-iam-list{max-height:200px;overflow-y:auto;margin:0 -${tok("space-3")}}
.hz-iam-trigger{display:flex;width:100%;box-sizing:border-box;align-items:center;gap:${tok("space-2")};padding:${tok("space-1")} ${tok("space-2")};font:inherit;color:${tok("foreground")};background:none;border:0;border-radius:${tok("radius-md")};text-align:left;cursor:pointer}
.hz-iam-trigger:hover,.hz-iam-trigger:focus-visible{background:${tok("muted")};outline:none}
.hz-iam-bal{flex:0 0 auto;white-space:nowrap;font-variant-numeric:tabular-nums;font-size:${tok("text-xs")};color:${tok("muted-foreground")}}
.hz-iam-bal[data-tone=danger]{color:${tok("state-error-text")}}
.hz-iam-avatar{display:flex;height:28px;width:28px;flex:0 0 auto;align-items:center;justify-content:center;border-radius:9999px;background:${tok("muted")};font-size:${tok("text-xs")};font-weight:${tok("weight-medium")};object-fit:cover}
.hz-iam-brand{display:flex;align-items:center;gap:${tok("space-2")};border-top:${border};padding:${tok("space-2")} ${tok("space-3")};font-size:${tok("text-xs")};color:${tok("muted-foreground")};text-decoration:none}
.hz-iam-mark{display:inline-flex;height:14px;width:14px;flex:0 0 auto}
.hz-iam-mark>svg{height:100%;width:100%}
.hz-iam-tenant{display:flex;align-items:center;gap:${tok("space-2")};font-size:${tok("text-sm")}}
.hz-iam-select{height:28px;max-width:100%;padding:0 ${tok("space-2")};font:inherit;font-size:${tok("text-sm")};color:${tok("foreground")};background:${tok("background")};border:${border};border-radius:${tok("radius-md")}}
.hz-iam-select:focus-visible{outline:none;border-color:${tok("ring")}}
.hz-iam-chip{border-radius:9999px;background:${tok("muted")};padding:2px ${tok("space-2")};font-size:${tok("text-xs")};color:${tok("muted-foreground")}}
@keyframes hz-iam-in{from{opacity:0;transform:translateY(4px) scale(.98)}to{opacity:1;transform:none}}
@media (prefers-reduced-motion:reduce){@keyframes hz-iam-in{from{opacity:1}to{opacity:1}}}
`;
  document.head.appendChild(el);
}

// ---------------------------------------------------------------------------
// OrgProjectSwitcher component
// ---------------------------------------------------------------------------

export interface OrgProjectSwitcherProps {
  organizations: Array<{ name: string; displayName?: string; owner?: string }>;
  currentOrgId: string | null;
  switchOrg: (orgId: string) => void;
  projects?: Array<{ name: string; displayName?: string; organization?: string; isDefault?: boolean }>;
  currentProjectId?: string | null;
  switchProject?: (projectId: string | null) => void;
  onTenantChange?: (orgId: string | null, projectId: string | null) => void;
  environment?: string | null;
  className?: string;
  alwaysShow?: boolean;
  /** See `OrgState.findOrgs` — turns the switcher into a searchable list. */
  findOrgs?: OrgFinder;
}

/**
 * Organization and project switcher component.
 *
 * @example
 * ```tsx
 * import { useOrganizations, OrgProjectSwitcher } from '@hanzo/iam/react'
 *
 * function Nav() {
 *   const orgState = useOrganizations()
 *   return <OrgProjectSwitcher {...orgState} />
 * }
 * ```
 */
export function OrgProjectSwitcher({
  organizations,
  currentOrgId,
  switchOrg,
  projects = [],
  currentProjectId = null,
  switchProject,
  onTenantChange,
  environment,
  className = "",
  alwaysShow = false,
  findOrgs,
}: OrgProjectSwitcherProps) {
  useEffect(() => {
    injectMenuCss();
  }, []);

  useEffect(() => {
    onTenantChange?.(currentOrgId, currentProjectId ?? null);
  }, [currentOrgId, currentProjectId, onTenantChange]);

  const handleOrgChange = useCallback(
    (e: { target: { value: string } }) => switchOrg(e.target.value),
    [switchOrg],
  );

  const handleProjectChange = useCallback(
    (e: { target: { value: string } }) => switchProject?.(e.target.value || null),
    [switchProject],
  );

  const envChip = environment
    ? createElement("span", { key: "env", className: "hz-iam-chip" }, environment)
    : null;

  if (!findOrgs && !alwaysShow && organizations.length <= 1 && projects.length <= 1) {
    if (organizations.length === 1) {
      const org = organizations[0];
      return createElement(
        "div",
        { className: `hz-iam-tenant ${className}` },
        createElement("span", { key: "org", className: "hz-iam-name" }, org.displayName || org.name),
        projects.length === 1
          ? [
              createElement("span", { className: "hz-iam-quiet", key: "sep" }, "/"),
              createElement("span", { key: "proj" }, projects[0].displayName || projects[0].name),
            ]
          : null,
        envChip,
      );
    }
    return null;
  }

  const projectSelect =
    projects.length > 0 && switchProject
      ? [
          createElement("span", { className: "hz-iam-quiet", key: "sep" }, "/"),
          createElement(
            "select",
            {
              key: "proj-select",
              className: "hz-iam-select",
              value: currentProjectId ?? "",
              onChange: handleProjectChange,
              "aria-label": "Switch project",
            },
            ...projects.map((proj) =>
              createElement("option", { key: proj.name, value: proj.name }, proj.displayName || proj.name),
            ),
          ),
        ]
      : null;

  // An admin surface reaches every tenant, so the set is SEARCHED, not listed.
  if (findOrgs) {
    return createElement(
      "div",
      { className },
      createElement(OrgFinderList, {
        key: "find",
        organizations,
        currentOrgId,
        switchOrg,
        findOrgs,
      }),
      projectSelect || envChip
        ? createElement("div", { key: "rest", className: "hz-iam-tenant" }, projectSelect, envChip)
        : null,
    );
  }

  return createElement(
    "div",
    { className: `hz-iam-tenant ${className}` },
    createElement(
      "select",
      {
        key: "org-select",
        className: "hz-iam-select",
        value: currentOrgId ?? "",
        onChange: handleOrgChange,
        "aria-label": "Switch organization",
      },
      ...organizations.map((org) =>
        createElement("option", { key: org.name, value: org.name }, org.displayName || org.name),
      ),
    ),
    projectSelect,
    envChip,
  );
}

/** The searchable org list — one field, one result list, one click to enter. */
function OrgFinderList({
  organizations,
  currentOrgId,
  switchOrg,
  findOrgs,
}: {
  organizations: Array<{ name: string; displayName?: string }>;
  currentOrgId: string | null;
  switchOrg: (orgId: string) => void;
  findOrgs: OrgFinder;
}) {
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState(organizations);
  const [busy, setBusy] = useState(false);

  // Debounced, and the LAST query wins: an out-of-order response from a slower
  // earlier search must never overwrite the one the user is actually reading.
  useEffect(() => {
    let live = true;
    const t = setTimeout(() => {
      setBusy(true);
      findOrgs(query)
        .then((found) => {
          if (live) setRows(found);
        })
        .catch(() => {
          /* the host's own honest state; the list simply does not change */
        })
        .finally(() => {
          if (live) setBusy(false);
        });
    }, query ? 200 : 0);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [query, findOrgs]);

  return createElement(
    "div",
    null,
    createElement("input", {
      key: "q",
      type: "search",
      className: "hz-iam-find",
      value: query,
      placeholder: "Find an organization",
      "aria-label": "Find an organization",
      onChange: (e: { target: { value: string } }) => setQuery(e.target.value),
    }),
    createElement(
      "div",
      { key: "rows", className: "hz-iam-list", role: "listbox" },
      rows.length === 0
        ? createElement(
            "div",
            { className: "hz-iam-lbl" },
            busy ? "Searching…" : "No organization matches that.",
          )
        : rows.map((org) =>
            createElement(
              "button",
              {
                key: org.name,
                type: "button",
                role: "option",
                className: "hz-iam-row",
                "aria-current": org.name === currentOrgId ? "true" : undefined,
                "aria-selected": org.name === currentOrgId,
                onClick: () => switchOrg(org.name),
              },
              createElement("span", { className: "hz-iam-grow" }, org.displayName || org.name),
              org.name === currentOrgId ? createElement("span", { className: "hz-iam-quiet" }, "✓") : null,
            ),
          ),
    ),
  );
}

// ---------------------------------------------------------------------------
// Embedded login surface — the DEFAULT the /react entry hands apps.
//
// `<Login>` is the one login UI: in-place social (popup + postMessage),
// in-place wallet (inline SIWx), and PKCE-bound email/password/OTP. Apps
// import it straight from "@hanzo/iam/react" instead of reaching for
// `signinRedirect`. `completePopupSignin` is the popup callback-page bridge,
// re-exported so the same entry covers the whole flow.
// ---------------------------------------------------------------------------

export {
  Login,
  SocialButton,
  WalletButton,
  EmailPasswordForm,
  OTPStep,
  useAuthMethods,
  OnboardingFlow,
  type LoginProps,
  type SocialButtonProps,
  type WalletButtonProps,
  type EmailPasswordFormProps,
  type OTPStepProps,
  type AuthMethod,
  type AuthMethodKind,
  type MethodsResponse,
  type MethodsView,
  type FlowProps,
} from "./views.js";

export { completePopupSignin } from "./browser.js";

// ---------------------------------------------------------------------------
// Identity: the one account menu
// ---------------------------------------------------------------------------

/**
 * Resolve the signed-in identity for display.
 *
 * Apps that reach into a single claim end up rendering a raw uuid when that claim
 * is absent (hanzo.app did). This walks every name IAM may carry — userinfo first,
 * then the token — and only ever falls back to the email's local part, never to an
 * id.
 */
export function useIamIdentity(): IamIdentity | null {
  return useIamIdentityFrom(useContext(IamContext));
}

/** The same resolution against a context that may be absent (no IamProvider). */
function useIamIdentityFrom(ctx: IamContextValue | null): IamIdentity | null {
  const user = ctx?.user ?? null;
  const accessToken = ctx?.accessToken ?? null;
  const isAuthenticated = ctx?.isAuthenticated ?? false;

  return useMemo(() => {
    if (!isAuthenticated) return null;
    return resolveIdentity(user as unknown as Record<string, unknown> | null, claimsOf(accessToken));
  }, [user, accessToken, isAuthenticated]);
}

// ---------------------------------------------------------------------------
// useUserTheme — the colour theme, per user
// ---------------------------------------------------------------------------

export interface UserTheme {
  /** The choice: an explicit mode or `system`. */
  mode: ThemeMode;
  /** What `mode` currently resolves to. */
  resolved: "light" | "dark";
  setMode: (mode: ThemeMode) => void;
}

/**
 * The colour theme as a per-USER setting, not a per-browser one.
 *
 * Keyed on the IAM subject, so two accounts on one machine keep their own choice
 * and signing in restores what that person picked. `system` follows the OS live.
 * Applied as `data-theme` on <html> plus Tailwind's `dark` class, so one control
 * drives both conventions and every app themes identically.
 *
 * Pass `persist` to ALSO store it wherever the app keeps user preferences (an IAM
 * user property, its own settings API) — the local copy is what paints on load.
 */
export function useUserTheme(options?: {
  subject?: string | null;
  persist?: (mode: ThemeMode) => void;
}): UserTheme {
  const ctx = useContext(IamContext);
  const claims = claimsOf(ctx?.accessToken ?? null);
  const subject =
    options?.subject ?? (typeof claims.sub === "string" ? (claims.sub as string) : null);

  const [mode, setModeState] = useState<ThemeMode>(() => readThemeMode(subject));
  const [resolved, setResolved] = useState<"light" | "dark">(() => applyTheme(mode));

  // A new identity brings its own choice with it.
  useEffect(() => {
    const stored = readThemeMode(subject);
    setModeState(stored);
    setResolved(applyTheme(stored));
  }, [subject]);

  // `system` means follow the OS as it changes, not only at load.
  useEffect(() => {
    if (mode !== "system") return;
    let mql: MediaQueryList;
    try {
      mql = window.matchMedia("(prefers-color-scheme: dark)");
    } catch {
      return;
    }
    const onChange = () => setResolved(applyTheme("system"));
    mql.addEventListener?.("change", onChange);
    return () => mql.removeEventListener?.("change", onChange);
  }, [mode]);

  const setMode = useCallback(
    (next: ThemeMode) => {
      setModeState(next);
      setResolved(applyTheme(next));
      try {
        localStorage.setItem(themeStorageKey(subject), next);
      } catch {
        /* private mode — the choice lasts for this session */
      }
      options?.persist?.(next);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subject, options?.persist],
  );

  return { mode, resolved, setMode };
}

// ---------------------------------------------------------------------------
// UserMenu — the one account control
// ---------------------------------------------------------------------------

/** One row in the menu. Give it a label (+ optional icon), or a fully custom node. */
export interface UserMenuItem {
  label?: string;
  /** Leading icon — any node, so each app keeps its own icon set. */
  icon?: ReactNode;
  /** Render this instead of a label row (a custom control, a nested trigger). */
  node?: ReactNode;
  /** Where it goes. Omit when using `onSelect`. */
  href?: string;
  /** Called instead of navigating. The menu closes first. */
  onSelect?: () => void;
  /** Open in a new tab. */
  external?: boolean;
  /** Tone: `danger` for destructive, `accent` for a call to action. */
  variant?: "default" | "danger" | "accent";
  /** Start a new group above this row. */
  separatorBefore?: boolean;
  /** Keyboard hint shown right-aligned (e.g. "⌘K"). */
  shortcut?: string;
  /** Section label rendered above this row. */
  sectionLabel?: string;
}

/**
 * Spend, as every Hanzo surface shows it. `state` is derived from the amount when
 * omitted — empty at zero, low under a dollar — so the colour and the "add funds"
 * prompt are the same everywhere instead of per-app arithmetic. The figure shows
 * in the menu and, at a glance, on the trigger itself.
 */
export interface UserMenuBalance {
  /** Amount in USD (already converted from credits). */
  amountUsd: number | string;
  state?: "ok" | "low" | "empty";
  /** Default: https://billing.hanzo.ai */
  topUpUrl?: string;
  label?: string;
  topUpLabel?: string;
}

/** Per-part class overrides, so one component wears each app's tokens. */
export interface UserMenuClassNames {
  root?: string;
  trigger?: string;
  triggerLabel?: string;
  menu?: string;
  section?: string;
  item?: string;
}

export interface UserMenuProps {
  /** App-specific entries (Files, Settings, Console, Docs …). */
  items?: UserMenuItem[];
  /** Org/team (and optional project) state — pass `useOrganizations()`. */
  orgState?: OrgState;
  /** Show the balance the way every surface shows it. */
  balance?: UserMenuBalance;
  /** Anything extra between identity and the switcher (a plan chip, a banner). */
  header?: ReactNode;
  /** Which way the menu opens. Default: `up` (the control usually sits at the bottom). */
  align?: "up" | "down";
  signInLabel?: string;
  signOutLabel?: string;
  className?: string;
  classNames?: UserMenuClassNames;
  /** Test hook for the trigger. Default: `nav-user`. */
  testId?: string;
  /**
   * Identity to show when the app's session does NOT live in `IamProvider`.
   *
   * hanzo.chat is the case: the login IS @hanzo/iam (PKCE to hanzo.id), but the
   * resulting session is a chat JWT its own AuthContext owns. Mounting IamProvider
   * beside that would make two sources of truth for one session, so instead pass
   * what you have — `resolveIdentity(user, claims)` turns it into the same shape,
   * and the menu, the switcher and the behavior stay identical across apps.
   */
  identity?: IamIdentity | null;
  /** Override the signed-in test (defaults to the IAM context). */
  isAuthenticated?: boolean;
  /** Override sign-in (defaults to the IAM redirect login). */
  onSignIn?: () => void;
  /** Override sign-out (defaults to clearing the IAM session). */
  onSignOut?: () => void;
  /** Override the loading state (defaults to the IAM context). */
  isLoading?: boolean;
  /**
   * Direct link to this app's user settings. Rendered first, where every Hanzo
   * surface puts it, so people stop hunting for it.
   */
  settingsUrl?: string;
  /** Direct link to usage/billing — the other thing everyone looks for. */
  usageUrl?: string;
  settingsLabel?: string;
  usageLabel?: string;
  /**
   * Colour theme control (light / dark / follow the system), per user. Pass
   * `useUserTheme()`; omit to hide the control.
   */
  theme?: UserTheme;
  /** "Invite members" — shown under the org section when given. */
  onInvite?: () => void;
  inviteLabel?: string;
  /**
   * Brand strip at the foot of the menu. Opt-in, because a Hanzo mark on a Lux or
   * Zoo surface is a white-label violation: pass the surface's own brand. Omit
   * `markSvg` to use the Hanzo mark from `@hanzo/logo`.
   */
  brand?: { name: string; markSvg?: string; href?: string };
}

/** Balance reads quiet when it is fine, and warns only when it is not. */
const BALANCE_TONE: Record<"ok" | "low" | "empty", string | undefined> = {
  ok: undefined,
  low: "danger",
  empty: "danger",
};

function balanceState(b: UserMenuBalance): "ok" | "low" | "empty" {
  if (b.state) return b.state;
  const n = typeof b.amountUsd === "string" ? Number.parseFloat(b.amountUsd) : b.amountUsd;
  if (!Number.isFinite(n) || n <= 0) return "empty";
  return n < 1 ? "low" : "ok";
}

/** The amount as a number, or null when a caller passed an unparseable string. */
function amountOf(b: UserMenuBalance): number | null {
  const n = typeof b.amountUsd === "string" ? Number.parseFloat(b.amountUsd) : b.amountUsd;
  return Number.isFinite(n) ? n : null;
}

/** The exact figure: grouped, two decimals, currency-marked. */
const money = (n: number): string =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

/**
 * The same figure at a glance. The trigger sits in a rail about 260px wide that
 * already carries an avatar and a name, so past four digits the cents cost more
 * room than they inform: 149738.29 reads as $149.7K, and `money` still has the
 * exact figure in the title and in the opened menu.
 */
const short = (n: number): string =>
  Math.abs(n) < 10000
    ? money(n)
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        notation: "compact",
        maximumFractionDigits: 1,
      }).format(n);

/** What the figure IS, in the caller's words. */
const balanceName = (b: UserMenuBalance): string => b.label ?? "Balance";

/** The exact figure for a title or a menu row, whatever the caller passed. */
const balanceExact = (b: UserMenuBalance): string => {
  const n = amountOf(b);
  return n === null ? String(b.amountUsd) : money(n);
};

/** The figure as the closed trigger shows it, where the room is scarce. */
const balanceGlance = (b: UserMenuBalance): string => {
  const n = amountOf(b);
  return n === null ? String(b.amountUsd) : short(n);
};

/**
 * The ONE account control: who you are, which org/team (and optionally project)
 * you are acting in, what you have left to spend, the app's own entries, and sign
 * in/out — all from IAM.
 *
 * Every app mounts this instead of rebuilding a menu; what differs between apps is
 * `items`, `balance` and `classNames`, never the identity, the switcher or the
 * behavior (click-away, Escape, close-before-navigate).
 *
 * @example
 * ```tsx
 * <UserMenu
 *   orgState={useOrganizations()}
 *   balance={{ amountUsd: credits / 1e6 }}
 *   items={[
 *     { label: 'Files', icon: <FileText />, onSelect: () => setShowFiles(true) },
 *     { label: 'Settings', icon: <Gear />, onSelect: () => setShowSettings(true) },
 *     { label: 'Console', href: 'https://console.hanzo.ai', external: true, separatorBefore: true },
 *   ]}
 * />
 * ```
 */
export function UserMenu({
  items = [],
  orgState,
  balance,
  header,
  align = "up",
  signInLabel = "Log in",
  signOutLabel = "Sign out",
  className = "",
  classNames = {},
  testId = "nav-user",
  identity: identityProp,
  isAuthenticated: isAuthenticatedProp,
  onSignIn,
  onSignOut,
  isLoading: isLoadingProp,
  settingsUrl,
  usageUrl,
  settingsLabel = "Settings",
  usageLabel = "Usage & billing",
  theme,
  onInvite,
  inviteLabel = "Invite members",
  brand,
}: UserMenuProps) {
  const ctx = useContext(IamContext);
  const ctxIdentity = useIamIdentityFrom(ctx);
  const [open, setOpen] = useState(false);

  // Either source works; an app that supplies identity needs no IamProvider.
  const identity = identityProp !== undefined ? identityProp : ctxIdentity;
  const isAuthenticated = isAuthenticatedProp ?? ctx?.isAuthenticated ?? false;
  const isLoading = isLoadingProp ?? ctx?.isLoading ?? false;
  const login = onSignIn ?? (() => void ctx?.login());
  const logout = onSignOut ?? (() => void ctx?.logout());

  // The control's own rules, once per document — and nothing else. It used to
  // also pull @hanzo/design's stylesheet off esm.sh, which made a cross-origin
  // request from every host that mounted an account menu (a CSP refuses it, and
  // this repo has already been bitten once by a CDN stylesheet being blocked).
  // It is no longer needed: every rule carries its authored value as a fallback,
  // so the tokens are a live override, not a dependency. An app that wants them
  // imports `@hanzo/design/styles.css` itself, where that decision belongs.
  useEffect(() => {
    try {
      injectMenuCss();
    } catch {
      /* SSR or a locked-down document */
    }
  }, []);

  // Click-away and Escape live here so no app has to remember them.
  const rootRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // The menu renders in a PORTAL. Inline, it is clipped by whatever the host app
  // wraps its nav in (chat's sidebar has overflow hidden, so the menu was in the
  // DOM and invisible) — a portal makes the control immune to every app's layout.
  // Anchored by real coordinates, never by a transform: the entrance keyframe
  // animates `transform`, so a menu that also POSITIONED itself with one had its
  // placement wiped the moment the animation ran — it opened downward off the
  // bottom of the screen while claiming to open up. Opening upward anchors
  // `bottom`; opening downward anchors `top`. Nothing to fight over.
  const [rect, setRect] = useState<{
    up: boolean;
    offset: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const place = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const gap = 8;
    const width = Math.max(r.width, 264);
    const left = Math.min(Math.max(gap, r.left), window.innerWidth - width - gap);

    // The control usually sits at the FOOT of a rail, where the menu opens
    // upward into whatever room is left. A menu with more entries than that room
    // used to run straight off the top of the screen, so the side is chosen by
    // measurement (keep `align` unless the other side is genuinely roomier) and
    // the height is capped to the room that side actually has — the menu scrolls
    // instead of leaving the viewport.
    const above = r.top - gap * 2;
    const below = window.innerHeight - r.bottom - gap * 2;
    const up = align === "up" ? above >= below : above > below;
    setRect({
      up,
      offset: up ? window.innerHeight - r.top + gap : r.bottom + gap,
      left,
      width,
      maxHeight: Math.max(160, up ? above : below),
    });
  }, [align]);

  useEffect(() => {
    if (!open) return;
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      const inTrigger = rootRef.current?.contains(t) ?? false;
      const inMenu = menuRef.current?.contains(t) ?? false;
      if (!inTrigger && !inMenu) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (isLoading) {
    return createElement("div", {
      className: `hz-iam-avatar ${className}`,
      "aria-hidden": "true",
    });
  }

  if (!isAuthenticated || !identity) {
    return createElement(
      "button",
      {
        type: "button",
        onClick: () => void login(),
        className: `${classNames.trigger ?? "hz-iam-trigger"} ${className}`,
      },
      signInLabel,
    );
  }

  const label = (text: string) =>
    createElement("div", { key: `l-${text}`, className: "hz-iam-lbl" }, text);

  const section = (key: string, ...children: ReactNode[]) =>
    createElement("div", { key, className: classNames.section ?? "hz-iam-sec" }, ...children);

  const row = (item: UserMenuItem, i: number) => {
    const body: ReactNode[] = item.node
      ? [item.node]
      : [
          item.icon ? createElement("span", { key: "ic" }, item.icon) : null,
          createElement("span", { key: "lb", className: "hz-iam-grow" }, item.label ?? ""),
          item.shortcut
            ? createElement("span", { key: "sc", className: "hz-iam-quiet" }, item.shortcut)
            : null,
        ];
    const common = {
      key: `i${i}`,
      role: "menuitem",
      className: classNames.item ?? "hz-iam-row",
      "data-tone": item.variant && item.variant !== "default" ? item.variant : undefined,
    };

    return item.href
      ? createElement(
          "a",
          {
            ...common,
            href: item.href,
            target: item.external ? "_blank" : undefined,
            rel: item.external ? "noopener noreferrer" : undefined,
            onClick: () => setOpen(false),
          },
          ...body,
        )
      : createElement(
          "button",
          {
            ...common,
            type: "button",
            onClick: () => {
              setOpen(false);
              item.onSelect?.();
            },
          },
          ...body,
        );
  };

  const groups: UserMenuItem[][] = [];
  let current: UserMenuItem[] = [];
  for (const item of items) {
    if (item.separatorBefore && current.length > 0) {
      groups.push(current);
      current = [];
    }
    current.push(item);
  }
  if (current.length > 0) groups.push(current);

  const group = (key: string, ...children: ReactNode[]) =>
    createElement("div", { key, className: "hz-iam-grp" }, ...children);

  const menu = open
    ? createElement(
        "div",
        {
          role: "menu",
          ref: menuRef,
          style: {
            // Non-negotiable base: an app class must not be able to hide the control.
            // chat's legacy `.popover-ui` sets opacity:0 (it animated in via ariakit's
            // lifecycle), which left the portalled menu present and invisible. Inline
            // wins over the class, so the widget always paints.
            opacity: 1,
            display: "block",
            position: "fixed",
            top: rect && !rect.up ? `${rect.offset}px` : rect ? "auto" : "-9999px",
            bottom: rect && rect.up ? `${rect.offset}px` : undefined,
            left: rect ? `${rect.left}px` : "-9999px",
            width: rect ? `${Math.max(rect.width, 264)}px` : undefined,
            maxHeight: rect ? `${rect.maxHeight}px` : undefined,
            overflowY: "auto",
            // The design system's own layering ladder, not a number invented here.
            zIndex: tok("z-popover"),
            visibility: rect ? "visible" : "hidden",
            animation: `hz-iam-in ${motion["duration-fast"]} ${motion["ease-out"]}`,
          },
          className: classNames.menu ?? "hz-iam-menu",
        },
        section(
          "who",
          createElement("div", { key: "n", className: "hz-iam-name" }, identity.name),
          identity.email
            ? createElement("div", { key: "e", className: "hz-iam-quiet" }, identity.email)
            : null,
        ),
        header ? section("header", header) : null,
        // The two links every surface is asked for, in the same place every time.
        settingsUrl || usageUrl
          ? group(
              "direct",
              settingsUrl ? row({ label: settingsLabel, href: settingsUrl }, 8001) : null,
              usageUrl ? row({ label: usageLabel, href: usageUrl }, 8002) : null,
            )
          : null,
        orgState && (orgState.organizations.length > 0 || orgState.findOrgs)
          ? section(
              "tenant",
              createElement(OrgProjectSwitcher, {
                key: "sw",
                organizations: orgState.organizations,
                currentOrgId: orgState.currentOrgId,
                switchOrg: orgState.switchOrg,
                projects: orgState.projects,
                currentProjectId: orgState.currentProjectId,
                switchProject: orgState.switchProject,
                findOrgs: orgState.findOrgs,
                alwaysShow: true,
              }),
              orgState.currentRole
                ? createElement("div", { key: "role", className: "hz-iam-quiet" }, orgState.currentRole)
                : null,
            )
          : null,
        balance
          ? (() => {
              const state = balanceState(balance);
              return section(
                "balance",
                createElement(
                  "div",
                  { key: "amt", "data-tone": BALANCE_TONE[state] },
                  `${balanceName(balance)}: ${balanceExact(balance)} USD`,
                  state === "empty" ? " (depleted)" : state === "low" ? " (low)" : "",
                ),
                state === "ok"
                  ? null
                  : createElement(
                      "a",
                      {
                        key: "top",
                        href: balance.topUpUrl ?? "https://billing.hanzo.ai",
                        target: "_blank",
                        rel: "noopener noreferrer",
                        className: "hz-iam-row",
                        "data-tone": "accent",
                      },
                      balance.topUpLabel ?? "Add funds",
                    ),
              );
            })()
          : null,
        onInvite ? group("invite", row({ label: inviteLabel, onSelect: onInvite }, 8003)) : null,
        ...groups.map((g, gi) => group(`g${gi}`, ...g.map((item, i) => row(item, gi * 1000 + i)))),
        theme
          ? group(
              "theme",
              label("Color theme"),
              ...(["light", "dark", "system"] as ThemeMode[]).map((m, i) =>
                row(
                  {
                    label: m === "system" ? "Sync with system" : m === "light" ? "Light" : "Dark",
                    shortcut: theme.mode === m ? "✓" : undefined,
                    onSelect: () => theme.setMode(m),
                  },
                  8100 + i,
                ),
              ),
            )
          : null,
        group("out", row({ label: signOutLabel, onSelect: () => logout() }, 9999)),
        brand
          ? createElement(
              brand.href ? "a" : "div",
              {
                key: "brand",
                href: brand.href,
                target: brand.href ? "_blank" : undefined,
                rel: brand.href ? "noopener noreferrer" : undefined,
                className: "hz-iam-brand",
              },
              createElement("span", {
                key: "mark",
                "aria-hidden": "true",
                className: "hz-iam-mark",
                style: { color: "currentColor" },
                dangerouslySetInnerHTML: {
                  __html:
                    brand.markSvg ??
                    `<svg viewBox="${MARK_VIEWBOX}" fill="currentColor" xmlns="http://www.w3.org/2000/svg">${MARK_PATHS}</svg>`,
                },
              }),
              createElement("span", { key: "name" }, brand.name),
            )
          : null,
      )
    : null;

  const avatar = identity.avatarUrl
    ? createElement("img", {
        src: identity.avatarUrl,
        alt: "",
        className: "hz-iam-avatar",
      })
    : createElement(
        "span",
        { className: "hz-iam-avatar", "aria-hidden": "true" },
        identity.initials,
      );

  return createElement(
    "div",
    {
      ref: rootRef,
      className: `${classNames.root ?? ""} ${className}`.trim() || undefined,
      style: { position: "relative" },
    },
    createElement(
      "button",
      {
        type: "button",
        "aria-haspopup": "menu",
        "aria-expanded": open ? "true" : "false",
        "aria-label": "Account",
        "data-testid": testId,
        onClick: () => setOpen((v) => !v),
        className: classNames.trigger ?? "hz-iam-trigger",
      },
      avatar,
      createElement(
        "span",
        { className: classNames.triggerLabel ?? "hz-iam-grow" },
        identity.name,
      ),
      // The wallet at a glance: given a balance, the trigger carries the figure.
      // The surfaces that mount this control pin it to the foot of a rail and
      // promise the balance is one glance away — a number that exists only
      // inside the opened menu is one click away, which is not the promise.
      // Quiet while funded, the warning tone when low or empty.
      //
      // It carries the NAME of the figure with it. A bare "$149,738.29" beside
      // an avatar says a quantity of dollars and not which quantity — spent,
      // earned, or left to spend — and the reader has to open the menu to learn
      // which, so the glance is worth nothing. The name is the caller's
      // (`label`), so a surface that grants credit and one that tracks a plan
      // each say their own word, and `title` keeps the exact figure reachable.
      balance
        ? createElement(
            "span",
            {
              className: "hz-iam-bal",
              "data-tone": BALANCE_TONE[balanceState(balance)],
              title: `${balanceName(balance)}: ${balanceExact(balance)} USD`,
            },
            `${balanceName(balance)} ${balanceGlance(balance)}`,
          )
        : null,
    ),
    // Portalled to <body>: no app's overflow can hide it.
    menu && typeof document !== "undefined" ? createPortal(menu, document.body) : null,
  );
}
